import { requestApplicationReload } from '@/utils/applicationUpdate';

const chunkUrlFromError = (error) => {
  const request = error?.request;
  if (typeof request === 'string' && /\/(?:js|css)\//.test(request)) return request;
  const filename = error?.filename;
  if (typeof filename === 'string' && /\/(?:js|css)\//.test(filename)) return filename;
  const match = String(error?.message || '').match(/https?:\/\/[^\s)]+\.(?:js|css)(?:\?[^\s)]*)?/);
  return match ? match[0] : '';
};

const isSameOriginChunk = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin
      && /^\/(?:js|css)\/.+\.(?:js|css)$/.test(parsed.pathname);
  } catch {
    return false;
  }
};

// Hashed /js files are cached for years. A truncated response stays in the HTTP
// cache, so a plain reload parses the same cut-off file again.
const refreshChunkInHttpCache = (url) => {
  if (!isSameOriginChunk(url) || typeof fetch !== 'function') {
    return Promise.resolve();
  }
  return fetch(url, { cache: 'reload', credentials: 'same-origin' }).then(() => {}, () => {});
};

export const isChunkLoadError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const filename = String(error?.filename || error?.request || '');
  const truncatedChunk = message.includes('unexpected end of input')
    && /\/js\/[^/]+\.js(?:$|\?)/.test(filename);
  return error?.name === 'ChunkLoadError'
    || error?.code === 'CSS_CHUNK_LOAD_FAILED'
    || message.includes('loading css chunk')
    || message.includes('loading chunk')
    || message.includes('failed to fetch dynamically imported module')
    || message.includes('importing a module script failed')
    || truncatedChunk;
};

export const recoverFromChunkLoadError = (error, { target } = {}) => {
  if (!isChunkLoadError(error) || typeof window === 'undefined') return false;
  let safeTarget;
  try {
    const parsed = new URL(target, window.location.origin);
    if (typeof target === 'string' && target.startsWith('/') && parsed.origin === window.location.origin) {
      safeTarget = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch { /* Keep the current route for malformed targets. */ }
  return requestApplicationReload({
    key: `hr:chunk-reload:${process.env.VUE_APP_BUILD_ID || 'dev'}:${chunkUrlFromError(error)}`,
    once: true,
    ...(safeTarget ? { target: safeTarget } : {}),
    beforeReload: () => {
      // Bound repair time, including when the connection disappears mid-request.
      let timer;
      const timeout = new Promise((resolve) => { timer = window.setTimeout(resolve, 4000); });
      return Promise.race([refreshChunkInHttpCache(chunkUrlFromError(error)), timeout])
        .finally(() => window.clearTimeout(timer));
    },
  });
};
